/**
 * PlaybackEngine — transporte unificado.
 *
 * Ele é o maestro: decide quem toca (player de referência, instrumental local,
 * faixas vocais, metrônomo), quando toca (no relógio do AudioContext) e
 * mantém tudo alinhado (SyncEngine + nudge de drift).
 *
 * Regra: a voz é SEMPRE agendada no relógio do AudioContext. O player de
 * referência (YouTube ou transporte interno) é quem recebe correção.
 */
import type { AudioEngine } from './AudioEngine.js';
import type { SyncEngine } from './SyncEngine.js';
import type { PlayerAdapter } from './player/types.js';
import { clamp } from '../utils/misc.js';
import { toStereoBuffer } from './buffers.js';

export type TransportStatus = 'stopped' | 'countdown' | 'playing' | 'paused';

export interface PlayVocalSpec {
  trackId: string;
  /** Posição na linha do tempo da faixa vocal onde começa a tocar. */
  fromPosition: number;
  useDenoised?: boolean;
  playbackRate?: number;
}

export interface PlayOptions {
  /** Tempo da música de onde começar (seek). */
  from?: number;
  /** Contagem: batidas e/ou segundos antes de começar. */
  countdown?: { beats?: number; seconds?: number; bpm?: number };
  /** Faixas vocais a tocar junto. */
  vocals?: PlayVocalSpec[];
  /** Tocar o instrumental local (modo arquivo) em vez do adapter. */
  instrumental?: boolean;
  metronome?: { bpm: number; gain: number; beats?: number };
  /** Callback por passo da contagem (para a UI 3-2-1). */
  onCountdownStep?: (info: { step: number; total: number; label: string; atPerf: number }) => void;
  /** Chamado quando o áudio realmente começa. */
  onStarted?: (info: { atPerf: number; ctxTime: number; mediaTime: number }) => void;
}

export interface PlaybackSnapshot {
  status: TransportStatus;
  mediaTime: number;
  ctxTime: number;
  driftMs: number;
  nudges: number;
  startedAtPerf: number;
}

export class PlaybackEngine {
  private audio: AudioEngine;
  private sync: SyncEngine;
  adapter: PlayerAdapter | null = null;
  status: TransportStatus = 'stopped';
  private monitorTimer: number | null = null;
  private startedAtPerf = 0;
  private startedAtCtx = 0;
  private startedAtMedia = 0;
  private pausedMediaTime = 0;
  private countdownAbort: (() => void) | null = null;
  private nudges = 0;
  private instrumentalBuffer: AudioBuffer | null = null;
  private lastOptions: PlayOptions = {};
  private activeVocalSources: AudioBufferSourceNode[] = [];
  onNudge?: (info: { correctionMs: number; reason: string }) => void;
  onStatusChange?: (status: TransportStatus) => void;
  onDrift?: (driftMs: number) => void;

  constructor(audio: AudioEngine, sync: SyncEngine) {
    this.audio = audio;
    this.sync = sync;
  }

  setAdapter(adapter: PlayerAdapter | null) {
    this.adapter = adapter;
  }

  setInstrumentalBuffer(buffer: AudioBuffer | null) {
    this.instrumentalBuffer = buffer;
    this.audio.setInstrumentalBuffer(buffer);
  }

  get mediaDuration(): number {
    const a = this.adapter?.getDuration() ?? 0;
    return Math.max(a, this.instrumentalBuffer?.duration ?? 0);
  }

  /** Tempo atual da música (relógio de referência). */
  getMediaTime(): number {
    if (this.status === 'playing') {
      if (this.adapter) return this.adapter.getCurrentTime();
      const elapsed = (performance.now() - this.startedAtPerf) / 1000;
      return Math.max(0, this.startedAtMedia + elapsed);
    }
    if (this.status === 'paused') return this.pausedMediaTime;
    return this.startedAtMedia;
  }

  private setStatus(s: TransportStatus) {
    this.status = s;
    this.onStatusChange?.(s);
  }

  /**
   * PLAY sincronizado.
   * Sequência: contagem -> (seek) -> play do adapter -> agendamento das vozes
   * no relógio do AudioContext compensando o atraso real do play.
   */
  async play(opts: PlayOptions = {}): Promise<void> {
    this.lastOptions = opts;
    const ctx = await this.audio.ensure();
    const from = Math.max(0, opts.from ?? this.getMediaTime());
    const useAdapter = !!this.adapter && !opts.instrumental;

    // 1) contagem (com o contexto já rodando, para o clique sair na hora)
    let countSec = 0;
    if (opts.countdown) {
      const beats = opts.countdown.beats ?? 0;
      const seconds = opts.countdown.seconds ?? 0;
      if (beats || seconds) {
        const bpm = opts.countdown.bpm ?? 90;
        countSec = (beats * 60) / bpm + seconds;
        await this.runCountdown(ctx.currentTime + 0.08, {
          beats,
          seconds,
          bpm,
          onStep: opts.onCountdownStep,
        });
      }
    }

    // 2) prepara a referência
    let mediaRequestPerf = performance.now();
    if (useAdapter && this.adapter) {
      this.adapter.seek(from);
      mediaRequestPerf = performance.now();
      await this.adapter.play();
    } else if (this.instrumentalBuffer) {
      mediaRequestPerf = performance.now();
    } else if (!useAdapter) {
      // Sem adapter e sem instrumental: nada a tocar.
      mediaRequestPerf = performance.now();
    }

    // 3) espera o play real (adapter avisa) ou usa o tempo pedido
    const startInfo = await this.waitForPlaybackStart(useAdapter, 900);
    const startDelayMs = useAdapter
      ? clamp(startInfo.delayMs, 0, 4000)
      : 0;
    if (useAdapter) {
      this.sync.playerStartDelayMs = startDelayMs;
      this.sync.record({
        type: 'play',
        ctxTime: ctx.currentTime,
        mediaTime: from,
        note: `startDelay=${Math.round(startDelayMs)}ms`,
      });
    }

    // 4) ancora a linha do tempo: quando a música está em `from`, o relógio de
    //    áudio vale `ctxStart`. Tudo deriva daí.
    const ctxStart = ctx.currentTime;
    const mediaStart = useAdapter ? (this.adapter?.getCurrentTime() ?? from) : from;
    this.startedAtPerf = performance.now();
    this.startedAtCtx = ctxStart;
    this.startedAtMedia = mediaStart;
    this.sync.setAnchor({ ctxTime: ctxStart, mediaTime: mediaStart, perf: this.startedAtPerf });

    // 5) instrumental local (quando for o caso)
    if (!useAdapter && this.instrumentalBuffer) {
      this.audio.playInstrumental({
        when: ctxStart,
        offsetSec: from,
        fadeInMs: 12,
        fadeOutMs: 0,
      });
    }

    // 6) metrônomo
    if (opts.metronome) {
      const beats = opts.metronome.beats ?? Math.ceil((this.mediaDuration - from) / (60 / opts.metronome.bpm));
      this.audio.scheduleMetronome(
        ctxStart,
        opts.metronome.bpm,
        Math.min(beats, 4096),
        opts.metronome.gain
      );
    }

    // 7) vozes — compensando o atraso real do play
    this.stopVocalSources();
    for (const v of opts.vocals ?? []) {
      const target = v.fromPosition + startDelayMs / 1000;
      const src = this.audio.playTrack(v.trackId, {
        when: ctxStart,
        offsetSec: Math.max(0, target),
        useDenoised: v.useDenoised,
        playbackRate: v.playbackRate,
      });
      if (src) this.activeVocalSources.push(src);
    }

    this.setStatus('playing');
    this.startMonitor();
    opts.onStarted?.({
      atPerf: this.startedAtPerf,
      ctxTime: ctxStart,
      mediaTime: mediaStart,
    });
    void countSec;
    void mediaRequestPerf;
  }

  private async waitForPlaybackStart(useAdapter: boolean, timeoutMs: number): Promise<{ delayMs: number }> {
    if (!useAdapter || !this.adapter) return { delayMs: 0 };
    const requested = this.adapter.getPlayRequestedAtPerf();
    const started = performance.now();
    // A API do YouTube avisa por onStateChange; aqui medimos o intervalo real.
    const anyAdapter = this.adapter as unknown as { lastStartDelayMs?: number };
    if (typeof anyAdapter.lastStartDelayMs === 'number' && anyAdapter.lastStartDelayMs > 0) {
      return { delayMs: anyAdapter.lastStartDelayMs };
    }
    const deadline = started + timeoutMs;
    while (performance.now() < deadline) {
      const d = (this.adapter as unknown as { lastStartDelayMs?: number }).lastStartDelayMs;
      if (typeof d === 'number' && d > 0) return { delayMs: d };
      if (this.adapter.getState() === 'playing') {
        return { delayMs: clamp(performance.now() - requested, 0, 4000) };
      }
      await new Promise((r) => setTimeout(r, 16));
    }
    return { delayMs: clamp(performance.now() - requested, 0, 4000) };
  }

  private async runCountdown(
    startCtx: number,
    opts: { beats: number; seconds: number; bpm: number; onStep?: PlayOptions['onCountdownStep'] }
  ): Promise<void> {
    const steps: { at: number; label: string }[] = [];
    const step = 60 / opts.bpm;
    let t = startCtx;
    for (let i = 0; i < opts.beats; i++) {
      steps.push({ at: t, label: String(opts.beats - i) });
      t += step;
    }
    for (let i = 0; i < opts.seconds; i++) {
      steps.push({ at: t, label: String(opts.seconds - i) });
      t += 1;
    }
    if (!steps.length) return;
    this.audio.scheduleCountdown(startCtx, {
      beats: opts.beats,
      seconds: opts.seconds,
      bpm: opts.bpm,
      gain: 0.45,
    });
    const total = steps.length;
    await new Promise<void>((resolve) => {
      let cancelled = false;
      this.countdownAbort = () => {
        cancelled = true;
        resolve();
      };
      const tick = () => {
        if (cancelled) return;
        const now = this.audio.currentTime;
        while (steps.length && steps[0].at <= now) {
          const s = steps.shift()!;
          opts.onStep?.({
            step: total - steps.length,
            total,
            label: s.label,
            atPerf: performance.now(),
          });
        }
        if (!steps.length) {
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
    this.countdownAbort = null;
  }

  private stopVocalSources() {
    for (const s of this.activeVocalSources) {
      try {
        s.stop();
        s.disconnect();
      } catch {
        /* noop */
      }
    }
    this.activeVocalSources = [];
  }

  private startMonitor() {
    this.stopMonitor();
    this.monitorTimer = window.setInterval(() => this.monitorTick(), 500);
  }

  private stopMonitor() {
    if (this.monitorTimer != null) {
      clearInterval(this.monitorTimer);
      this.monitorTimer = null;
    }
  }

  /**
   * Loop de sincronização: amostra os dois relógios, estima drift e corrige a
   * referência quando o erro passa do limiar (nudge curto, imperceptível).
   */
  private monitorTick() {
    if (this.status !== 'playing') return;
    const ctxTime = this.audio.currentTime;
    const mediaTime = this.getMediaTime();
    this.sync.sample({ ctxTime, mediaTime, perf: performance.now() });
    const err = this.sync.drift.errorMs();
    this.onDrift?.(err);
    const decision = this.sync.evaluateDrift();
    if (decision.action === 'nudge-media' && this.adapter) {
      const target = Math.max(0, mediaTime - decision.correctionMs / 1000);
      this.adapter.seek(target);
      this.nudges++;
      this.sync.driftCorrectionMs = -decision.correctionMs;
      this.sync.record({
        type: 'nudge',
        ctxTime,
        mediaTime,
        note: decision.reason,
      });
      this.onNudge?.({ correctionMs: decision.correctionMs, reason: decision.reason });
      this.sync.setAnchor({ ctxTime, mediaTime: target, perf: performance.now() });
    }
  }

  pause(): void {
    if (this.status !== 'playing') return;
    const ctx = this.audio.ctx;
    this.pausedMediaTime = this.getMediaTime();
    if (ctx) {
      this.audio.stopInstrumental(ctx.currentTime + 0.02);
      this.stopVocalSources();
    }
    this.adapter?.pause();
    this.setStatus('paused');
    this.stopMonitor();
    this.sync.record({
      type: 'pause',
      ctxTime: this.audio.currentTime,
      mediaTime: this.pausedMediaTime,
    });
  }

  /** Retomar do ponto exato (com a mesma compensação de atraso do play). */
  async resume(): Promise<void> {
    if (this.status !== 'paused') return;
    await this.play({ ...this.lastOptions, from: this.pausedMediaTime, countdown: undefined });
    this.sync.record({
      type: 'resume',
      ctxTime: this.audio.currentTime,
      mediaTime: this.pausedMediaTime,
    });
  }

  stop(): void {
    const ctx = this.audio.ctx;
    this.adapter?.stop();
    if (ctx) this.audio.stopInstrumental(ctx.currentTime + 0.01);
    this.stopVocalSources();
    this.countdownAbort?.();
    this.setStatus('stopped');
    this.stopMonitor();
    this.pausedMediaTime = 0;
    this.startedAtMedia = 0;
    this.sync.record({ type: 'seek', ctxTime: this.audio.currentTime, mediaTime: 0, note: 'stop' });
  }

  seek(sec: number): void {
    const target = clamp(sec, 0, this.mediaDuration || sec);
    const wasPlaying = this.status === 'playing';
    if (wasPlaying) this.pause();
    this.adapter?.seek(target);
    this.pausedMediaTime = target;
    this.startedAtMedia = target;
    this.sync.record({ type: 'seek', ctxTime: this.audio.currentTime, mediaTime: target });
    if (wasPlaying) void this.resume();
  }

  snapshot(): PlaybackSnapshot {
    return {
      status: this.status,
      mediaTime: this.getMediaTime(),
      ctxTime: this.audio.currentTime,
      driftMs: this.sync.drift.errorMs(),
      nudges: this.nudges,
      startedAtPerf: this.startedAtPerf,
    };
  }

  /** Converte o material de referência local (transporte interno) em buffer. */
  loadInternalReference(left: Float32Array, right: Float32Array, sampleRate: number) {
    const ctx = this.audio.ctx;
    if (!ctx) return null;
    const buf = toStereoBuffer(ctx, left, right, sampleRate);
    this.setInstrumentalBuffer(buf);
    return buf;
  }

  dispose() {
    this.stop();
    this.adapter?.destroy();
    this.adapter = null;
  }
}
